import { useEffect, useState } from "react";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  pointerWithin,
} from "@dnd-kit/core";
import Swal from "sweetalert2";
import { MdEvent, MdEco } from "react-icons/md";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import Appbar from "./components/appbar";
import { Box } from "@chakra-ui/layout";
import MapaGeral from "./components/maps/mapa-geral";
import { useFiltersBaseLists } from "core/features/filters/hooks/useFiltersBaseLists";
import { useMenuFilterIndex } from "core/features/filters/hooks/useMenuFilterIndex";
import { useFilters } from "core/features/filters/store";
import { TipoMapaTalhoes } from "core/features/maps/typings";
import { CenterMapContextProvider } from "core/features/maps/center-map/CenterMap.context";
import LoadingCard from "./components/LoadingCard";
import useVincularPlanejamentosTalhoes from "./components/maps/base-map/hooks/useVincularPlanejamentosTalhoes";
import useTalhoesStore from "./components/maps/base-map/hooks/useTalhoesStore";
import useGetPlanejamentoOperacional from "./hooks/useGetPlanejamentoOperacional";
import { showError, showConfirm } from "infra/helpers/alerta";
import { useTalhoesMap, useTalhoesMapOS, useTalhoesPlanejamento } from "./hooks/useTalhoesMap";
import {
  MaterialIcons,
  OperationItem,
  OperationContent,
  OperationDetails,
  OperationName,
  OperationMeta,
  MetaItem,
  OperationAmount,
} from "./styles";

interface OperationCardOverlayProps {
  plano: any;
  quantidade: number;
}

function OperationCardOverlay({ plano, quantidade }: OperationCardOverlayProps) {
  return (
    <OperationItem>
      <OperationAmount>{quantidade}</OperationAmount>
      <OperationContent>
        <OperationDetails>
          <OperationName>{plano.descricao}</OperationName>
          <OperationMeta>
            <MetaItem>
              <MaterialIcons color="#757575"><MdEvent /></MaterialIcons>
              <span>{format(new Date(plano.dataCriacao), "MMM yyyy", { locale: ptBR })}</span>
            </MetaItem>
            <MetaItem>
              <MaterialIcons color="#757575"><MdEco /></MaterialIcons>
              <span>{plano.ocupacao}</span>
            </MetaItem>
          </OperationMeta>
        </OperationDetails>
      </OperationContent>
    </OperationItem>
  );
}

function PlanejamentoOperacional() {

  const { indexOpen } = useMenuFilterIndex();
  const {
    updateField,
    filters: { unidade, safra, menuName, tabBarSessaoPlanejamentoOperacional },
  } = useFilters();

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  const [filters, setFilters] = useState({ unidadeid: unidade?.id, safraid: safra?.id });

  const { mutateAsync } = useVincularPlanejamentosTalhoes();
  const { talhaoIds, allTalhoesIds, planoOperacionalIds, setPlanoOperacionalIds, setTalhaoIds } = useTalhoesStore();
  const { data: planoOperacionalData, refetch } = useGetPlanejamentoOperacional(filters);
  const { data, isFetching, refetch: refetchMapa } = useTalhoesPlanejamento();
  const { data: dataMap, isFetching: isFetchingMapa } = useTalhoesMap();
  const { data: dataMapOS, isFetching: isFetchingMapaOS } = useTalhoesMapOS();

  const [loading, setLoading] = useState(false);
  const [activePlano, setActivePlano] = useState<any>();
  const [quantidade, setQuantidade] = useState(0);

  useFiltersBaseLists();

  function handleDragStart(event: any) {
    const data = event.active.data.current;
    const selected = data.selectedItems.length;
    setActivePlano(data.planoOperacional);
    setQuantidade(selected);
  }

  function showSuccessMessage(quantidadeTalhoesPorPlano: any[]) {
    const quantidadeVinculados = quantidadeTalhoesPorPlano.filter(i => i.quantidadeAVincular > 0).length;
    const vinculados = quantidadeTalhoesPorPlano
      .filter(i => i.quantidadeAVincular > 0)
      .reduce((acc, item) => acc = acc + `<p style="margin-bottom: 8px;">${item.plano} (${item.quantidadeAVincular} ${item.quantidadeAVincular === 1 ? "talhão" : "talhões"})</p>`, "");
    const naoVinculados = quantidadeTalhoesPorPlano
      .filter(i => i.quantidadeVinculada > 0)
      .reduce((acc, item) => acc = acc + `<p style="margin-bottom: 8px;">${item.plano} já estava vinculado (${item.quantidadeVinculada} ${item.quantidadeVinculada === 1 ? "talhão" : "talhões"})</p>`, "");

    const todosVinculados = quantidadeTalhoesPorPlano.every(i => i.quantidadeAVincular === 0);
    const tituloComVinculo = `${quantidadeVinculados} planejamento${quantidadeVinculados > 1 ? "s" : ""} vinculado${quantidadeVinculados > 1 ? "s" : ""}`;
    const titulo = todosVinculados ? "Nenhum novo vínculo criado" : tituloComVinculo;

    Swal.fire({
      width: 700,
      icon: todosVinculados ? "info" : "success",
      title: todosVinculados ? "Atenção" : "Sucesso",
      iconColor: todosVinculados ? "#ff9800" : "#009540",
      confirmButtonText: "OK",
      showCancelButton: false,
      confirmButtonColor: "#009540",
      html: `
        <p><b>${titulo}</b></p>
        ${!!vinculados ? "<br/>" : ""}
        ${vinculados}
        ${!!naoVinculados ? "<br/>" : ""}
        ${naoVinculados}
      `,
    });
  }

  async function handleDragEnd(event: any) {
    const { over } = event;
    setActivePlano(undefined);
    if (!over) return;

    const message = `Deseja vincular o${planoOperacionalIds.length > 1 ? "s" : ""} planejamento${planoOperacionalIds.length > 1 ? "s" : ""} ao${talhaoIds.length > 1 ? "s" : ""} ${talhaoIds.length > 1 ? "talhões" : "talhão"}?`;
    const confirmation = await showConfirm(message);
    if (!confirmation.isConfirmed) return;

    setLoading(true);
    try {
      const planosOperacionais = planoOperacionalData?.planosOperacionais;
      const planosSelecionados = planosOperacionais?.filter((p: any) => planoOperacionalIds?.some(i => i.id === p?.planoOperacionalId));

      const talhoes = data?.features?.flatMap((item: any) => item?.properties);
      const talhoesSelecionados = talhaoIds.length > 0 ? talhoes?.filter((t: any) => talhaoIds?.some(i => i.id === t?.id)) : talhoes;

      const quantidadeTalhoesPorPlano = planosSelecionados.map((plano: any) => {
        const quantidade = talhoesSelecionados.filter((t: any) => t?.planosoperacionais?.every((p: any) => p.PlanoOperacionalId !== plano?.planoOperacionalId)).length;
        return {
          plano: plano?.descricao,
          quantidadeAVincular: quantidade,
          quantidadeVinculada: talhoesSelecionados?.length - quantidade,
        }
      });

      const body = {
        planoOperacionalIds: planoOperacionalIds.map(p => p.id),
        talhaoIds: talhaoIds.length > 0 ? talhaoIds.map(t => t.id) : allTalhoesIds,
      }
      await mutateAsync(body);
      await refetch();
      await refetchMapa();
      setTalhaoIds([]);
      setPlanoOperacionalIds([]);
      showSuccessMessage(quantidadeTalhoesPorPlano);
    } catch (error) {
      console.log(error);
      showError("Erro ao vincular os planejamentos aos talhões");
    } finally {
      setLoading(false);
    }
  }

  function handleDragCancel() {
    setActivePlano(undefined);
  }

  useEffect(() => {
    updateField("tipoMapa", TipoMapaTalhoes.PlanejamentoOperacional);
  }, [indexOpen, updateField]);

  return (
    <>
      {loading && <LoadingCard />}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <Box w="100%">
          <Appbar
            filters={filters}
            setFilters={setFilters}
            planoOperacionalData={planoOperacionalData}
            dataMapOS={dataMapOS}
            refetch={refetch}
            refetchMap={refetchMapa}
          />
          <CenterMapContextProvider>
            <MapaGeral
              filters={filters}
              data={data}
              refetch={refetch}
              dataMap={menuName === "Gestão de O.S." && tabBarSessaoPlanejamentoOperacional !== 'relatorios' ? dataMapOS : dataMap}
              planoOperacionalData={planoOperacionalData}
              isFetching={isFetching}
              isFetchingMap={menuName === "Gestão de O.S." && tabBarSessaoPlanejamentoOperacional !== 'relatorios' ? isFetchingMapaOS : isFetchingMapa}
            />
          </CenterMapContextProvider>
        </Box>

        <DragOverlay dropAnimation={null}>
          {activePlano && <OperationCardOverlay plano={activePlano} quantidade={quantidade} />}
        </DragOverlay>
      </DndContext>
    </>
  );
}

export default PlanejamentoOperacional;
